import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  SearchIcon,
  AddIcon,
  SpreadsheetIcon,
  SpinnerIcon,
  DeleteIcon,
  EditIcon,
  CopyIcon,
  LockIcon,
} from "@/lib/icons";
import { TaskTemplateViewer } from "@/components/taskTemplate/TaskTemplateViewer";
import { CreateTaskTemplateDialog } from "@/components/taskTemplate/CreateTaskTemplateDialog";
import { SaveAsTemplateSetDialog } from "@/components/taskTemplate/SaveAsTemplateSetDialog";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { useTaskTemplates, type TaskTemplateSet } from "@/hooks/useTaskTemplates";
import { useAuth } from "@/contexts/AuthContext";

/** At most three hanke names inline; the rest collapse into a counter. */
function linkedProjectsLabel(set: TaskTemplateSet): string {
  const names = (set.linkedProjects ?? []).map((p) => p.name);
  if (names.length === 0) return "—";
  if (names.length <= 3) return names.join(", ");
  return `${names.slice(0, 3).join(", ")} +${names.length - 3} muuta`;
}

export function Tehtavapohjat() {
  const {
    taskTemplateSets,
    loading,
    creating,
    isSubmitting,
    createError,
    resetCreateError,
    updateTemplateSet,
    deleteTemplateSet,
    createTemplateSetWithHierarchy,
    duplicateTemplateSet,
    saveTemplateSetTasks,
    savingTasks,
  } = useTaskTemplates();

  const { dbUser } = useAuth();
  const isAdmin = dbUser?.accessLevel === "ADMIN";

  const [search, setSearch] = useState("");

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSet, setSelectedSet] = useState<TaskTemplateSet | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    description: "",
  });

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // View dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingSet, setViewingSet] = useState<TaskTemplateSet | null>(null);

  // Track IDs of templates being deleted for optimistic UI
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  // Save-as dialog state
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copySource, setCopySource] = useState<TaskTemplateSet | null>(null);

  // Unsaved edits inside the view dialog, guarded on close
  const [viewerDirty, setViewerDirty] = useState(false);
  const [confirmDiscardOnClose, setConfirmDiscardOnClose] = useState(false);
  const [setToDelete, setSetToDelete] = useState<TaskTemplateSet | null>(null);

  const closeViewDialog = () => {
    setViewerDirty(false);
    setConfirmDiscardOnClose(false);
    setViewDialogOpen(false);
  };

  const requestCloseViewDialog = () => {
    if (viewerDirty) {
      setConfirmDiscardOnClose(true);
      return;
    }
    closeViewDialog();
  };

  const [editError, setEditError] = useState<string | null>(null);

  const filteredSets = useMemo(() => {
    let sets = taskTemplateSets;
    if (!sets) return [];
    if (search) {
      const lower = search.toLowerCase();
      sets = sets.filter(
        (set: TaskTemplateSet) =>
          set.name.toLowerCase().includes(lower)
      );
    }
    // Filter out items being deleted
    sets = sets.filter((set: TaskTemplateSet) => !deletingIds.has(set.id));
    return sets;
  }, [taskTemplateSets, search, deletingIds]);

  const openEdit = (set: TaskTemplateSet) => {
    setSelectedSet(set);
    setEditFormData({
      name: set.name,
      description: set.description || "",
    });
    setEditError(null);
    setEditDialogOpen(true);
  };

  const openCopy = (set: TaskTemplateSet) => {
    setCopySource(set);
    setCopyDialogOpen(true);
  };

  const handleCopyConfirm = async (name: string, description: string | null) => {
    if (!copySource) return;
    const result = await duplicateTemplateSet({
      id: copySource.id,
      name,
      description,
    });
    if (!result?.success) {
      throw new Error(result?.message || "Kopiointi epäonnistui");
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedSet) return;
    try {
      const result = await updateTemplateSet({
        id: selectedSet.id,
        version: selectedSet.version,
        name: editFormData.name,
        description: editFormData.description || null,
      });
      if (result && result.success === false) {
        setEditError(result.message || "Päivitys epäonnistui");
        return;
      }
      setEditDialogOpen(false);
    } catch (error) {
      console.error("Failed to update template set:", error);
      setEditError(
        error instanceof Error ? error.message : "Päivitys epäonnistui"
      );
    }
  };

  const handleDelete = async (set: TaskTemplateSet) => {
    // Immediately remove from UI
    setDeletingIds(prev => new Set(prev).add(set.id));
    
    try {
      const result = await deleteTemplateSet(set.id);
      
      if (!result?.success) {
        const errorMsg = result?.message || "Tuntematon virhe";
        alert("Poistaminen epäonnistui: " + errorMsg);
        // Restore to UI on failure
        setDeletingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(set.id);
          return newSet;
        });
      }
    } catch (error: any) {
      const errorMsg = error.message || error.graphQLErrors?.[0]?.message || "Tuntematon virhe";
      alert("Poistaminen epäonnistui: " + errorMsg);
      // Restore to UI on failure
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(set.id);
        return newSet;
      });
    }
  };

  const openView = (set: TaskTemplateSet) => {
    setViewingSet(set);
    setViewDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-5 w-48 mt-2" />
          </div>
        </div>
        <Skeleton className="h-10 w-64" />
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {["Nimi", "Hankkeet", "Luonut", "Luotu", "Toiminnot"].map((h) => (
                  <TableHead key={h}>{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {[1, 2, 3, 4, 5].map((i) => (
                <TableRow key={i}>
                  {[1, 2, 3, 4, 5].map((j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-5 w-24" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="sticky top-0 z-20 flex flex-wrap items-center gap-3 px-4 py-3 border-b bg-background shrink-0 -mx-6 -mt-6">
        <h1 className="text-lg font-semibold mr-2">Tehtäväpohjat</h1>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Hae tehtäväpohjia..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} size="sm" className="ml-auto">
          <AddIcon className="mr-2 h-4 w-4" />
          Luo uusi tehtäväpohja
        </Button>
      </div>
      <div className="space-y-6 pt-3">

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nimi</TableHead>
              <TableHead>Hankkeet</TableHead>
              <TableHead>Luonut</TableHead>
              <TableHead>Luotu</TableHead>
              <TableHead className="text-right">Toiminnot</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredSets.map((set: TaskTemplateSet) => (
              <TableRow
                key={set.id}
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => openView(set)}
              >
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <SpreadsheetIcon className="h-4 w-4 text-muted-foreground" />
                    {set.name}
                    {!set.isEditable && (
                      <LockIcon
                        className="h-3.5 w-3.5 text-muted-foreground"
                        aria-label="Käytössä hankkeessa"
                      />
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {linkedProjectsLabel(set)}
                </TableCell>
                <TableCell>
                  {set.createdBy 
                    ? `${set.createdBy.firstName || ''} ${set.createdBy.lastName || ''}`.trim() || set.createdBy.email
                    : "–"}
                </TableCell>
                <TableCell>
                  {(() => {
                    const date = new Date(set.createdAt);
                    const dateStr = date.toLocaleDateString("fi-FI", {
                      day: "numeric",
                      month: "numeric",
                      year: "numeric",
                    });
                    const hours = date.getHours().toString().padStart(2, '0');
                    const minutes = date.getMinutes().toString().padStart(2, '0');
                    return `${dateStr} klo ${hours}:${minutes}`;
                  })()}
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(set)}
                      disabled={!set.isEditable}
                      title={
                        set.isEditable
                          ? "Muokkaa"
                          : "Tehtäväpohja on käytössä — tee kopio muokataksesi"
                      }
                    >
                      <EditIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openCopy(set)}
                      title="Tallenna nimellä"
                    >
                      <CopyIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSetToDelete(set)}
                      disabled={!isAdmin || !set.isEditable}
                      title={
                        !isAdmin
                          ? "Vain ylläpitäjä voi poistaa tehtäväpohjan"
                          : set.isEditable
                          ? "Poista"
                          : "Tehtäväpohja on käytössä hankkeessa"
                      }
                    >
                      <DeleteIcon className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filteredSets.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center text-muted-foreground py-8"
                >
                  Ei tehtäväpohjia
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Muokkaa tehtäväpohjaa</DialogTitle>
            <DialogDescription>Päivitä tehtäväpohjan nimi ja kuvaus</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Nimi</Label>
              <Input
                id="edit-name"
                value={editFormData.name}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, name: e.target.value })
                }
                placeholder="Tehtäväpohjan nimi"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-description">Kuvaus</Label>
              <Textarea
                id="edit-description"
                value={editFormData.description}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, description: e.target.value })
                }
                placeholder="Tehtäväpohjan kuvaus"
                rows={3}
              />
            </div>
            {editError && (
              <p className="text-sm text-destructive">{editError}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
            >
              Peruuta
            </Button>
            <Button onClick={handleEditSubmit} disabled={!editFormData.name || creating}>
              {creating && <SpinnerIcon className="mr-2 h-4 w-4 animate-spin" />}
              Tallenna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            requestCloseViewDialog();
            return;
          }
          setViewDialogOpen(true);
        }}
      >
        <DialogContent className="sm:max-w-[1172px] flex flex-col h-[90vh]">
          <DialogHeader>
            <DialogTitle>{viewingSet?.name}</DialogTitle>
            <DialogDescription>
              <div className="flex items-center gap-2">
                <span>Luonut:</span>
                <span className="font-medium">
                  {viewingSet?.createdBy 
                    ? `${viewingSet.createdBy.firstName || ''} ${viewingSet.createdBy.lastName || ''}`.trim() || viewingSet.createdBy.email
                    : "–"}
                </span>
                <span className="text-muted-foreground">|</span>
                <span>
                  {viewingSet?.createdAt && (() => {
                    const date = new Date(viewingSet.createdAt);
                    const dateStr = date.toLocaleDateString("fi-FI", {
                      day: "numeric",
                      month: "numeric",
                      year: "numeric",
                    });
                    const hours = date.getHours().toString().padStart(2, '0');
                    const minutes = date.getMinutes().toString().padStart(2, '0');
                    return `${dateStr} klo ${hours}:${minutes}`;
                  })()}
                </span>
              </div>
              {viewingSet?.description && (
                <div className="mt-2 text-sm text-muted-foreground">
                  {viewingSet.description}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-hidden py-4">
            {viewingSet && (
              <TaskTemplateViewer
                taskTemplateSetId={viewingSet.id}
                maxHeight="100%"
                editable={viewingSet.isEditable}
                lockedByProjects={(viewingSet.linkedProjects ?? []).map(
                  (p) => p.name
                )}
                saving={savingTasks}
                onDirtyChange={setViewerDirty}
                onSave={(payload) =>
                  saveTemplateSetTasks({
                    taskTemplateSetId: viewingSet.id,
                    ...payload,
                  })
                }
              />
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={requestCloseViewDialog}>
              Sulje
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!setToDelete}
        onOpenChange={(open) => {
          if (!open) setSetToDelete(null);
        }}
        title="Poista tehtäväpohja"
        description={
          <>
            Haluatko varmasti poistaa tehtäväpohjan{" "}
            <strong>"{setToDelete?.name}"</strong>? Tätä ei voi perua.
          </>
        }
        confirmLabel="Poista"
        destructive
        onConfirm={() => {
          const target = setToDelete;
          setSetToDelete(null);
          if (target) void handleDelete(target);
        }}
      />

      <ConfirmDialog
        open={confirmDiscardOnClose}
        onOpenChange={setConfirmDiscardOnClose}
        title="Sulje tehtäväpohja"
        description="Tallentamattomat muutokset menetetään. Haluatko sulkea ikkunan?"
        confirmLabel="Sulje tallentamatta"
        destructive
        onConfirm={closeViewDialog}
      />

      <SaveAsTemplateSetDialog
        open={copyDialogOpen}
        onOpenChange={setCopyDialogOpen}
        sourceName={copySource?.name ?? ""}
        onConfirm={handleCopyConfirm}
      />

      <CreateTaskTemplateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        isSubmitting={isSubmitting}
        createError={createError}
        resetCreateError={resetCreateError}
        createTemplateSetWithHierarchy={createTemplateSetWithHierarchy}
      />
      </div>
    </div>
  );
}
